import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Zap, 
  Euro, 
  Package, 
  Home, 
  Wrench, 
  Grid, 
  CheckCircle, 
  Download,
  Mail
} from 'lucide-react';
import { useConfigurator } from '../ConfiguratorProvider';
import { toast } from '@/hooks/use-toast';

export const ConfigurationSummary = () => {
  const { configuration, setIsOpen } = useConfigurator();

  const calculateTotalPrice = () => {
    let total = 0;
    
    if (configuration.panel) {
      total += configuration.panel.price * configuration.panelCount;
    }
    if (configuration.inverter) {
      total += configuration.inverter.price;
    }
    if (configuration.mountingSystem) {
      total += configuration.mountingSystem.price * configuration.panelCount;
    }
    
    return total;
  };

  const calculateDiscountedPrice = () => {
    return Math.round(calculateTotalPrice() * 0.9); // 10% discount
  };

  const handleRequestQuote = () => {
    toast({
      title: "Offerte aangevraagd",
      description: "We nemen binnen 24 uur contact met je op voor een persoonlijke offerte.",
    });
    setIsOpen(false);
  };

  const handleDownloadConfig = () => {
    toast({
      title: "Configuratie gedownload",
      description: "Je configuratie is opgeslagen als PDF.",
    });
  };

  if (!configuration.panel || !configuration.inverter || !configuration.roofType || 
      !configuration.mountingSystem || !configuration.installationMethod) {
    return (
      <div className="text-center py-12">
        <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-medium mb-2">Configuratie niet compleet</h3>
        <p className="text-muted-foreground">
          Ga terug en voltooi alle stappen om je overzicht te zien
        </p>
      </div>
    );
  }

  const totalPower = (configuration.panel.wattage * configuration.panelCount) / 1000;
  const originalPrice = calculateTotalPrice();
  const finalPrice = calculateDiscountedPrice();
  const discount = originalPrice - finalPrice;

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-2xl font-bold mb-2">Jouw Zonnepaneel Configuratie</h3>
        <p className="text-muted-foreground">
          Overzicht van je gepersonaliseerde zonnepaneel systeem
        </p>
      </div>

      {/* System Overview */}
      <Card className="border-primary/20 bg-primary/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            Systeem Overzicht
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-primary">{configuration.panelCount}</div>
              <div className="text-sm text-muted-foreground">Zonnepanelen</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-primary">{totalPower} kWp</div>
              <div className="text-sm text-muted-foreground">Totaal vermogen</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-primary">~{Math.round(totalPower * 950)} kWh</div>
              <div className="text-sm text-muted-foreground">Jaarlijkse opbrengst</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Component Details */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="h-4 w-4" />
              Componenten
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <div className="font-medium">{configuration.panel.brand}</div>
                <div className="text-sm text-muted-foreground">
                  {configuration.panel.model} ({configuration.panelCount}x)
                </div>
              </div>
              <div className="text-right">
                <div className="font-medium">€{(configuration.panel.price * configuration.panelCount).toLocaleString()}</div>
              </div>
            </div>
            
            <Separator />
            
            <div className="flex justify-between items-center">
              <div>
                <div className="font-medium">{configuration.inverter.brand}</div>
                <div className="text-sm text-muted-foreground">
                  {configuration.inverter.model}
                  {configuration.inverter.hybrid && (
                    <Badge className="ml-2 text-xs">Hybrid</Badge>
                  )}
                </div>
              </div>
              <div className="text-right">
                <div className="font-medium">€{configuration.inverter.price.toLocaleString()}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Wrench className="h-4 w-4" />
              Installatie
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <div className="font-medium flex items-center gap-2">
                  <Home className="h-4 w-4" />
                  {configuration.roofType.name}
                </div>
                <div className="text-sm text-muted-foreground">
                  {configuration.mountingSystem.name}
                </div>
              </div>
              <div className="text-right">
                <div className="font-medium">€{(configuration.mountingSystem.price * configuration.panelCount).toLocaleString()}</div>
              </div>
            </div>
            
            <Separator />
            
            <div className="flex justify-between items-center">
              <div>
                <div className="font-medium flex items-center gap-2">
                  <Grid className="h-4 w-4" />
                  {configuration.installationMethod.name}
                </div>
                <div className="text-sm text-muted-foreground">
                  {configuration.installationMethod.description}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pricing */}
      <Card className="border-green-200 bg-green-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Euro className="h-5 w-5 text-green-600" />
            Prijsoverzicht
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span>Subtotaal</span>
              <span className="line-through text-muted-foreground">€{originalPrice.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-green-600 font-medium">
              <span>Sol-Vi korting (10%)</span>
              <span>-€{discount.toLocaleString()}</span>
            </div>
            <Separator />
            <div className="flex justify-between text-xl font-bold">
              <span>Totaalprijs</span>
              <span>€{finalPrice.toLocaleString()}</span>
            </div>
            <div className="text-sm text-muted-foreground">
              ✓ 0% BTW | ✓ 10 jaar garantie | ✓ Gratis montage
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Button onClick={handleRequestQuote} className="flex-1">
          <Mail className="mr-2 h-4 w-4" />
          Offerte Aanvragen
        </Button>
        <Button variant="outline" onClick={handleDownloadConfig}>
          <Download className="mr-2 h-4 w-4" />
          Download PDF
        </Button>
      </div>

      {/* Next Steps */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            Volgende Stappen
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-primary rounded-full"></div>
              <span>Persoonlijke offerte binnen 24 uur</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-primary rounded-full"></div>
              <span>Dakonderzoek en locatie-inspectie</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-primary rounded-full"></div>
              <span>Installatie binnen 4-6 weken</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};